// frontend/src/utils/homeTabHelpers.js
import { SPORT_CATEGORIES, CATEGORY_TO_CATS, getCategoryForSport } from '../config/sportsConfig';

// The first allowed league key in a category, after filtering to enabledSports.
// This is the league shown by default when the category is selected (e.g. NFL for
// "American Football"). Returns null if the category has no allowed leagues.
export function getPrimaryLeagueKey(category, enabledSports) {
  const sportKeys = SPORT_CATEGORIES[category] || [];
  const allowedKeys = enabledSports ? sportKeys.filter(k => enabledSports.includes(k)) : sportKeys;
  return allowedKeys[0] || null;
}

// Count of leagues allowed in a category after filtering to enabledSports.
// Categories with count <= 1 never show a league switcher (nothing to switch to).
export function getAllowedLeagueCount(category, enabledSports) {
  const sportKeys = SPORT_CATEGORIES[category] || [];
  const allowedKeys = enabledSports ? sportKeys.filter(k => enabledSports.includes(k)) : sportKeys;
  return allowedKeys.length;
}

// odds-api.io names dynamic-sport leagues as "<Group> - <Rest>" — the group half
// is a real distinction (esports: the game title; MMA/boxing: the promotion;
// tennis: the tour tier; cricket/darts/table tennis: the country or "International").
// Splits on the FIRST " - " only, since the remainder can itself contain one
// (e.g. "Rainbow Six - APAC North - Stage 2" -> group "Rainbow Six").
export function splitLeagueName(fullName) {
  const idx = fullName.indexOf(' - ');
  if (idx === -1) return { group: fullName, sub: fullName };
  return { group: fullName.slice(0, idx), sub: fullName.slice(idx + 3) };
}

// Groups a flat list of league names by their splitLeagueName() prefix.
// Returns a Map (insertion order = first-seen order of each group), each
// value a sorted array of the full league names under that group.
export function groupLeaguesByPrefix(leagueNames) {
  const groups = new Map();
  for (const name of leagueNames) {
    const { group } = splitLeagueName(name);
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group).push(name);
  }
  for (const list of groups.values()) list.sort();
  return groups;
}

// Whether grouping is worth showing at all: real consolidation (fewer groups
// than leagues) and more than one group (otherwise there's nothing to pick
// between). A sport with 3 leagues in 3 different groups gets the flat list;
// once there are enough concurrent events for a group to have 2+ leagues,
// the grouped view kicks in automatically.
export function shouldGroupLeagues(groups, totalLeagueCount) {
  return groups.size > 1 && groups.size < totalLeagueCount;
}

// Resolves which SPORT_CATEGORIES key an activeSport value belongs to. Handles both
// permanent sport keys (e.g. 'americanfootball_nfl') and synthetic dynamic-league
// keys produced by the GoalServe active-leagues flow (e.g. 'dynamic::football_10::12345').
// Returns null for falsy input, 'all', or a key that doesn't resolve to any category.
export function getCategoryOfActiveSport(activeSport) {
  if (!activeSport || activeSport === 'all') return null;
  if (activeSport.startsWith('dynamic::')) {
    const catCode = activeSport.split('::')[1];
    const match = Object.entries(CATEGORY_TO_CATS).find(([, cats]) => cats.includes(catCode));
    return match ? match[0] : null;
  }
  const category = getCategoryForSport(activeSport);
  return category === 'Other' ? null : category;
}
